import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  getClinicList,
  saveLandingPage,
  getLandingPagesList,
  getLandingPageDomains,
  getLandingPageCities,
} from "../../services/landingPages";

const teal = "#16a09a";
const tealLight = "#e6f7f5";

const AD_SOURCES: { label: string; value: string }[] = [
  { label: "SEO", value: "seo" },
  { label: "GMB", value: "gmb" },
  { label: "Google Ads", value: "google-ads" },
  { label: "Email", value: "email" },
  { label: "Meta Ads", value: "meta-ads" },
  { label: "Xcare Loyalty", value: "xcare-loyalty" },
];

const ZONES = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

interface SelectDropdownProps {
  label: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (v: string) => void;
  placeholder?: string;
  optional?: boolean;
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  optional,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
        {label}{" "}
        {optional ? (
          <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
        ) : (
          <span style={{ color: "#e53e3e" }}>*</span>
        )}
      </div>
      <div ref={ref} style={{ position: "relative" }}>
        <div
          onClick={() => setOpen(!open)}
          style={{
            border: "1px solid #d1d5db",
            borderRadius: 8,
            padding: "10px 14px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#fff",
            fontSize: 14,
          }}
        >
          <span style={{ color: !selected && !value ? "#9ca3af" : undefined }}>
            {(selected?.label ?? value) || (placeholder ?? "Select...")}
          </span>
          <i
            className={`bi bi-chevron-${open ? "up" : "down"}`}
            style={{ color: "#6b7280", fontSize: 12 }}
          />
        </div>
        {open && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              zIndex: 100,
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  padding: "10px 14px",
                  cursor: "pointer",
                  fontSize: 14,
                  background: opt.value === value ? tealLight : "#fff",
                  color: opt.value === value ? teal : "#374151",
                  fontWeight: opt.value === value ? 600 : 400,
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface Clinic {
  id: number;
  uniqueId?: string;
  name: string;
  city?: string;
  clinicName?: string;
}

interface LandingPageEntry {
  id?: number;
  source?: string;
  city?: string;
  domain?: string;
  zone?: string | number;
  /** Pre-built path segment after /clinics/ if API provides it */
  slug?: string;
  url_path?: string;
  clinicIds?: string;
  clinic_ids?: string;
  clinics?:
    | Array<{
        id?: number;
        name?: string;
        clinicName?: string;
        [key: string]: any;
      }>
    | string;
  clinic_names?: string;
  clinicNames?: string;
  url?: string;
  generated_url?: string;
  generatedUrl?: string;
  created_at?: string;
  createdAt?: string;
  [key: string]: any;
}

const slugifySegment = (raw: string) =>
  raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Maps domain title to URL segment (e.g. Dental → dentist, matches /clinics/dentist-in-oakville) */
const domainTitleToPathSegment = (title: string) => {
  const s = slugifySegment(title);
  if (s === "dental") return "dentist";
  return s;
};

const resolveDomainTitle = (
  row: LandingPageEntry,
  domainOptions: { id: string; title: string }[]
): string => {
  const d = row.domain;
  if (d === undefined || d === null || d === "") return "";
  const byId = domainOptions.find((o) => o.id === String(d));
  if (byId?.title) return byId.title;
  return String(d);
};

const getLandingPageBaseUrl = (): string => {
  if (typeof window === "undefined") return "https://xcarepro.com";

  const hostname = window.location.hostname.toLowerCase();

  if (
    hostname === "localhost" ||
    hostname === "staging.data-driven.io" ||
    hostname.includes("staging.xcarepro.com") ||
    hostname.includes("stagingus.xcarepro.com")
  ) {
    return "https://staging.xcarepro.com";
  }

  if (
    hostname === "xcarepro.com" ||
    hostname === "www.xcarepro.com" ||
    hostname === "dashboard.xcarepro.com" ||
    hostname === "dashboard.us.xcarepro.com" ||
    hostname === "data-driven.io"
  ) {
    return "https://xcarepro.com";
  }

  return window.location.origin;
};

/** Builds shareable landing URL: {origin}/clinics/{slug}?source=&zone= */
const buildCopyableLandingUrl = (
  row: LandingPageEntry,
  domainOptions: { id: string; title: string }[]
): string => {
  const origin = getLandingPageBaseUrl();

  const explicit =
    row.slug ?? row.url_path ?? row.url_slug ?? row.landing_slug;
  if (explicit && String(explicit).trim()) {
    const path = String(explicit).replace(/^\//, "").replace(/^clinics\//, "");
    const qs = new URLSearchParams();
    qs.set("source", String(row.source ?? "google-ads"));
    qs.set("zone", String(row.zone ?? "1"));
    return `${origin}/clinics/${path}?${qs.toString()}`;
  }

  const city = row.city?.trim();
  if (!city) return "";
  const domainTitle = resolveDomainTitle(row, domainOptions);
  if (!domainTitle) return "";
  const pathSeg = `${domainTitleToPathSegment(domainTitle)}-in-${slugifySegment(city)}`;
  const qs = new URLSearchParams();
  qs.set("source", String(row.source ?? "google-ads"));
  qs.set("zone", String(row.zone ?? "1"));
  return `${origin}/clinics/${pathSeg}?${qs.toString()}`;
};

const getClinicDisplay = (row: LandingPageEntry): string => {
  // Try clinics array first
  if (Array.isArray(row.clinics) && row.clinics.length > 0) {
    return row.clinics
      .map((c) => c.clinicName ?? c.name ?? "")
      .filter(Boolean)
      .join(", ");
  }
  // Try string fields
  const nameStr = row.clinic_names ?? row.clinicNames;
  if (nameStr) return nameStr;
  // Fall back to IDs
  const ids = row.clinicIds ?? row.clinic_ids;
  if (ids)
    return ids
      .split(",")
      .map((id: string) => `Clinic ${id.trim()}`)
      .join(", ");
  return "—";
};

export const URLGeneration = () => {
  // Form state
  const [source, setSource] = useState("google-ads");
  const [domain, setDomain] = useState("");
  const [zone, setZone] = useState("");
  const [primaryCity, setPrimaryCity] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [selectedCities, setSelectedCities] = useState<string[]>([]);

  // Domains/Cities from API
  const [domainOptions, setDomainOptions] = useState<
    { id: string; title: string }[]
  >([]);
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [filterCityOptions, setFilterCityOptions] = useState<string[]>([]);

  // Clinics from API
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [clinicSearch, setClinicSearch] = useState("");
  const [selectedClinicIds, setSelectedClinicIds] = useState<number[]>([]);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");

  // List state
  const [listPage, setListPage] = useState(1);
  const [listData, setListData] = useState<LandingPageEntry[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [filterDomain, setFilterDomain] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [copiedRowKey, setCopiedRowKey] = useState<string | number | null>(
    null
  );

  // Holds clinic IDs from handleEdit so fetchClinics can restore them after the async fetch
  const editClinicIdsRef = useRef<number[] | null>(null);

  const RECORD_PER_PAGE = 10;

  // Fetch domains once
  useEffect(() => {
    let mounted = true;
    getLandingPageDomains()
      .then((res: any) => {
        const data = res?.data ?? res;
        const list: any[] = Array.isArray(data?.domain)
          ? data.domain
          : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];
        const mapped = list
          .map((d: any) => ({
            id: String(d?.id ?? d?.domainId ?? d?.value ?? ""),
            title: String(d?.title ?? d?.name ?? d?.label ?? ""),
          }))
          .filter((d: any) => d.id && d.title);
        if (mounted && mapped.length > 0) {
          setDomainOptions(mapped);
          if (!domain || !mapped.some((m: any) => m.id === domain)) {
            setDomain(mapped[0].id);
          }
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch cities whenever domain changes
  useEffect(() => {
    let mounted = true;
    if (!domain) return;
    getLandingPageCities(domain)
      .then((res: any) => {
        const data = res?.data ?? res;
        const list: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.cities)
          ? data.cities
          : [];
        const names = list
          .map((c: any) => c?.name ?? c?.city ?? c)
          .filter(Boolean)
          .map((v: any) => String(v));
        if (mounted && names.length > 0) {
          setCityOptions(names);
          // reset city selections if they're not valid for this domain
          setPrimaryCity((prev) => (prev && names.includes(prev) ? prev : ""));
          setSelectedCities((prev) => prev.filter((c) => names.includes(c)));
        } else if (mounted) {
          setCityOptions([]);
          setPrimaryCity("");
          setSelectedCities([]);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [domain]);

  // Fetch cities for filter dropdown when filterDomain changes
  useEffect(() => {
    let mounted = true;
    if (!filterDomain) {
      setFilterCityOptions([]);
      return;
    }
    getLandingPageCities(filterDomain)
      .then((res: any) => {
        const data = res?.data ?? res;
        const list: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.cities)
          ? data.cities
          : [];
        const names = list
          .map((c: any) => c?.name ?? c?.city ?? c)
          .filter(Boolean)
          .map((v: any) => String(v));
        if (mounted && names.length > 0) {
          setFilterCityOptions(names);
          setFilterCity((prev) => (prev && names.includes(prev) ? prev : ""));
        } else if (mounted) {
          setFilterCityOptions([]);
          setFilterCity("");
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [filterDomain]);

  // Fetch clinics whenever relevant fields change
  const fetchClinics = useCallback(async () => {
    if (!primaryCity || !domain) return;
    setClinicsLoading(true);
    try {
      const searchcity =
        selectedCities.length > 0
          ? [primaryCity, ...selectedCities].join(",")
          : primaryCity;
      const res: any = await getClinicList({
        source,
        city: primaryCity,
        domain,
        zone,
        searchcity,
      });
      const data = res?.data ?? res;
      const rawList: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.clinics)
        ? data.clinics
        : Array.isArray(data?.data)
        ? data.data
        : [];
      // Normalize id to number so selectedClinicIds comparisons are always number === number
      const list: Clinic[] = rawList.map((c: any) => ({ ...c, id: Number(c.id) }));
      setClinics(list);
      // Normalize to numbers to handle APIs that return string IDs
      const validIds = new Set(list.map((c) => Number(c.id)));
      if (editClinicIdsRef.current !== null) {
        // Edit mode: restore the IDs from the edited row, filtered to what's in this list
        const restored = editClinicIdsRef.current.filter((id) =>
          validIds.has(id)
        );
        editClinicIdsRef.current = null;
        setSelectedClinicIds(restored);
      } else {
        setSelectedClinicIds((prev) =>
          prev.filter((id) => validIds.has(Number(id)))
        );
      }
    } catch {
      editClinicIdsRef.current = null;
      setClinics([]);
      setSelectedClinicIds([]);
    } finally {
      setClinicsLoading(false);
    }
  }, [source, domain, zone, primaryCity, selectedCities]);

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  // Fetch landing pages list (API expects domain name, not id)
  const fetchList = useCallback(async () => {
    setListLoading(true);
    const domainName =
      filterDomain &&
      domainOptions.find((d) => d.id === filterDomain)?.title;
    try {
      const res: any = await getLandingPagesList({
        page: listPage,
        record: RECORD_PER_PAGE,
        domain: domainName || undefined,
        city: filterCity || undefined,
      });
      const data = res?.data ?? res;
      const entries: LandingPageEntry[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.results)
        ? data.results
        : [];
      const total = data?.total ?? data?.count ?? entries.length;
      setListData(entries);
      setListTotal(total);
    } catch {
      setListData([]);
      setListTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [listPage, filterDomain, filterCity, domainOptions]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleResetFilters = () => {
    setFilterDomain("");
    setFilterCity("");
    setListPage(1);
  };

  const handleCopyLandingUrl = async (
    row: LandingPageEntry,
    rowKey: string | number
  ) => {
    const url = buildCopyableLandingUrl(row, domainOptions);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedRowKey(rowKey);
      window.setTimeout(() => setCopiedRowKey(null), 2000);
    } catch {
      // fallback for older browsers
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopiedRowKey(rowKey);
        window.setTimeout(() => setCopiedRowKey(null), 2000);
      } catch {
        /* ignore */
      }
    }
  };

  const filteredCities = cityOptions.filter((c) =>
    c.toLowerCase().includes(citySearch.toLowerCase())
  );

  const toggleCity = (city: string) => {
    if (city === primaryCity) return; // disabled in multi-select
    setSelectedCities((prev) =>
      prev.includes(city) ? prev.filter((c) => c !== city) : [...prev, city]
    );
  };

  const handleEdit = (row: LandingPageEntry) => {
    // Extract clinic IDs — API may return them as a CSV string or as a clinics array
    const idsStr = row.clinicIds ?? row.clinic_ids ?? "";
    let parsedIds: number[] = [];
    if (idsStr) {
      parsedIds = idsStr
        .split(",")
        .map((id: string) => Number(id.trim()))
        .filter(Boolean);
    } else if (Array.isArray(row.clinics) && row.clinics.length > 0) {
      parsedIds = row.clinics
        .map((c) => Number(c.id ?? c.uniqueId))
        .filter(Boolean);
    }

    // Store in ref BEFORE state changes so fetchClinics can restore them after the async fetch
    editClinicIdsRef.current = parsedIds;

    setSource(row.source ?? "google-ads");
    setDomain(row.domain ?? "");
    setZone(row.zone ? String(row.zone) : "");
    setPrimaryCity(row.city ?? "");
    setSelectedCities([]);
    setSelectedClinicIds(parsedIds);
    setSaveSuccess(false);
    setSaveError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleClinic = (id: number) => {
    setSelectedClinicIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const getClinicName = (c: Clinic) => c.clinicName ?? c.name ?? "";

  const filteredClinics = clinics.filter((c) =>
    getClinicName(c).toLowerCase().includes(clinicSearch.toLowerCase())
  );

  const handleSave = async () => {
    if (!primaryCity || selectedClinicIds.length === 0) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError("");
    try {
      await saveLandingPage({
        source,
        city: primaryCity,
        domain,
        zone,
        clinicIds: selectedClinicIds.join(","),
      });
      fetchList();
      // Reset form then show success banner
      handleReset();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err?.message ?? "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    editClinicIdsRef.current = null;
    setSource("google-ads");
    setDomain(domainOptions[0]?.id ?? "");
    setZone("");
    setPrimaryCity("");
    setCitySearch("");
    setSelectedCities([]);
    setClinics([]);
    setClinicSearch("");
    setSelectedClinicIds([]);
    setSaveSuccess(false);
    setSaveError("");
  };

  const totalPages = Math.ceil(listTotal / RECORD_PER_PAGE);

  return (
    <>
      {/* Page Header */}
      <section className="middle-navbar px-30">
        <div className="p-0 container-fluid">
          <div className="d-flex justify-content-between">
            <div className="w-100">
              <div className="d-flex flex-row items-center gap-3 w-100">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: tealLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginBottom: 14,
                  }}
                >
                  <i
                    className="bi bi-link-45deg"
                    style={{ color: teal, fontSize: 20 }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    New Landing Page
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 300 }}>
                    Generate a URL like
                    xcarepro.com/clinics/dental-in-mississauga
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="px-30" style={{ paddingTop: 28, paddingBottom: 40 }}>
        <div
          style={{
            display: "flex",
            gap: 24,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          {/* ── LEFT: Create Form ── */}
          <div style={{ flex: "0 0 520px", minWidth: 320 }}>
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: "28px 28px 20px",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  marginBottom: 20,
                  color: "#111827",
                }}
              >
                Create Landing Page
              </div>

              {/* Ad Source */}
              <SelectDropdown
                label="Ad Source"
                value={source}
                options={AD_SOURCES}
                onChange={setSource}
              />

              {/* Domain */}
              <SelectDropdown
                label="Domain / Vertical"
                value={domain}
                options={domainOptions.map((d) => ({
                  label: d.title,
                  value: d.id,
                }))}
                onChange={(v) => {
                  setDomain(v);
                  setClinics([]);
                  setSelectedClinicIds([]);
                }}
              />

              {/* City (single select) */}
              <SelectDropdown
                label="City"
                value={primaryCity}
                options={cityOptions.map((c) => ({ label: c, value: c }))}
                onChange={(v) => {
                  setPrimaryCity(v);
                  setSelectedCities([]);
                  setClinics([]);
                  setSelectedClinicIds([]);
                }}
                placeholder="Select city..."
              />

              {/* Zone */}
              <SelectDropdown
                label="Zone"
                value={zone}
                options={ZONES.map((z) => ({ label: `Zone ${z}`, value: z }))}
                onChange={setZone}
                placeholder="Select Zone"
                optional
              />

              {/* Select Cities */}
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    marginBottom: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <i
                    className="bi bi-geo-alt"
                    style={{ color: teal, fontSize: 15 }}
                  />
                  Select Cities <span style={{ color: "#e53e3e" }}>*</span>
                </div>
                <div
                  style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}
                >
                  Add nearby cities to include border clinics (sent as
                  searchcity to API).
                </div>

                <div
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px 8px 0 0",
                    padding: "9px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#fff",
                  }}
                >
                  <i
                    className="bi bi-search"
                    style={{ color: "#9ca3af", fontSize: 13 }}
                  />
                  <input
                    type="text"
                    placeholder="Search cities..."
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      flex: 1,
                      fontSize: 13,
                      background: "transparent",
                      color: "#374151",
                    }}
                  />
                </div>

                <div
                  style={{
                    border: "1px solid #d1d5db",
                    borderTop: "none",
                    borderRadius: "0 0 8px 8px",
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  {filteredCities.map((city, idx) => {
                    const checked = selectedCities.includes(city);
                    const isDisabled = city === primaryCity;
                    return (
                      <div
                        key={city}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 14px",
                          borderBottom:
                            idx < filteredCities.length - 1
                              ? "1px solid #f3f4f6"
                              : "none",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          background: isDisabled ? "#f9fafb" : "#fff",
                          opacity: isDisabled ? 0.5 : 1,
                        }}
                        onClick={() => !isDisabled && toggleCity(city)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 4,
                              border: checked ? "none" : "2px solid #d1d5db",
                              background: checked ? teal : "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {checked && (
                              <i
                                className="bi bi-check"
                                style={{
                                  color: "#fff",
                                  fontSize: 13,
                                  lineHeight: 1,
                                }}
                              />
                            )}
                          </div>
                          <span style={{ fontSize: 14, color: "#374151" }}>
                            {city}
                          </span>
                        </div>
                        {isDisabled && (
                          <span
                            style={{
                              background: "#e5e7eb",
                              color: "#6b7280",
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "2px 10px",
                              borderRadius: 20,
                            }}
                          >
                            Selected above
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedCities.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    {selectedCities.map((city) => (
                      <span
                        key={city}
                        style={{
                          padding: "4px 14px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          background: "#e5e7eb",
                          color: "#374151",
                          userSelect: "none",
                        }}
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Select Clinics */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                  Select Clinics to Display{" "}
                  <span style={{ color: "#e53e3e" }}>*</span>
                </div>
                {primaryCity && (
                  <div style={{ fontSize: 12, color: teal, marginBottom: 10 }}>
                    {clinicsLoading
                      ? "Loading clinics..."
                      : `${clinics.length} clinic${
                          clinics.length !== 1 ? "s" : ""
                        } found`}
                  </div>
                )}

                <div
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px 8px 0 0",
                    padding: "9px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#fff",
                  }}
                >
                  <i
                    className="bi bi-search"
                    style={{ color: "#9ca3af", fontSize: 13 }}
                  />
                  <input
                    type="text"
                    placeholder="Search clinics..."
                    value={clinicSearch}
                    onChange={(e) => setClinicSearch(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      flex: 1,
                      fontSize: 13,
                      background: "transparent",
                      color: "#374151",
                    }}
                  />
                </div>

                <div
                  style={{
                    border: "1px solid #d1d5db",
                    borderTop: "none",
                    borderRadius: "0 0 8px 8px",
                    maxHeight: 220,
                    overflowY: "auto",
                  }}
                >
                  {clinicsLoading ? (
                    <div style={{ padding: "20px 14px", textAlign: "center" }}>
                      <div
                        className="spinner-border spinner-border-sm"
                        style={{ color: teal }}
                      />
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 13,
                          color: "#6b7280",
                        }}
                      >
                        Fetching clinics...
                      </span>
                    </div>
                  ) : filteredClinics.length === 0 ? (
                    <div
                      style={{
                        padding: "20px 14px",
                        fontSize: 13,
                        color: "#9ca3af",
                        textAlign: "center",
                      }}
                    >
                      {!primaryCity
                        ? "Select a city first to see clinics"
                        : "No clinics found"}
                    </div>
                  ) : (
                    filteredClinics.map((clinic, idx) => {
                      const checked = selectedClinicIds.includes(clinic.id);
                      const name = getClinicName(clinic);
                      return (
                        <div
                          key={clinic.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 14px",
                            borderBottom:
                              idx < filteredClinics.length - 1
                                ? "1px solid #f3f4f6"
                                : "none",
                            cursor: "pointer",
                            background: "#fff",
                          }}
                          onClick={() => toggleClinic(clinic.id)}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: 4,
                                border: checked ? "none" : "2px solid #d1d5db",
                                background: checked ? teal : "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {checked && (
                                <i
                                  className="bi bi-check"
                                  style={{
                                    color: "#fff",
                                    fontSize: 13,
                                    lineHeight: 1,
                                  }}
                                />
                              )}
                            </div>
                            <span style={{ fontSize: 14, color: "#374151" }}>
                              {name}
                            </span>
                            {clinic.city && (
                              <span
                                style={{
                                  background: "#f3f4f6",
                                  color: "#6b7280",
                                  fontSize: 11,
                                  padding: "2px 8px",
                                  borderRadius: 12,
                                  fontWeight: 500,
                                }}
                              >
                                {clinic.city}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {selectedClinicIds.length > 0 && (
                  <div
                    style={{
                      fontSize: 12,
                      color: teal,
                      marginTop: 8,
                      fontWeight: 500,
                    }}
                  >
                    {selectedClinicIds.length} clinic
                    {selectedClinicIds.length !== 1 ? "s" : ""} selected
                  </div>
                )}
              </div>

              {/* Save success / error feedback */}
              {saveSuccess && (
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 12,
                    fontSize: 13,
                    color: "#15803d",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <i className="bi bi-check-circle-fill" />
                  Landing page saved successfully!
                </div>
              )}
              {saveError && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 12,
                    fontSize: 13,
                    color: "#dc2626",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <i className="bi bi-exclamation-circle-fill" />
                  {saveError}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <button
                  onClick={handleSave}
                  disabled={
                    saving || !primaryCity || selectedClinicIds.length === 0
                  }
                  style={{
                    flex: 1,
                    background:
                      saving || !primaryCity || selectedClinicIds.length === 0
                        ? "#9ca3af"
                        : teal,
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "13px 24px",
                    fontWeight: 600,
                    fontSize: 15,
                    cursor:
                      saving || !primaryCity || selectedClinicIds.length === 0
                        ? "not-allowed"
                        : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  {saving && (
                    <span className="spinner-border spinner-border-sm" />
                  )}
                  Save Landing Page
                </button>
                <button
                  onClick={handleReset}
                  style={{
                    background: "#fff",
                    color: "#374151",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    padding: "13px 24px",
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: "pointer",
                    minWidth: 90,
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Saved Landing Pages ── */}
          <div style={{ flex: 1, minWidth: 320 }}>
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: "24px",
              }}
            >
              {/* Header row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 16,
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div>
                  <div
                    style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}
                  >
                    Saved Landing Pages
                  </div>
                  {listTotal > 0 && (
                    <div
                      style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}
                    >
                      {listTotal} page{listTotal !== 1 ? "s" : ""} total
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <select
                    value={filterDomain}
                    onChange={(e) => {
                      setFilterDomain(e.target.value);
                      setListPage(1);
                    }}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      padding: "7px 12px",
                      fontSize: 13,
                      color: "#374151",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">All Domains</option>
                    {domainOptions.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.title}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filterCity}
                    onChange={(e) => {
                      setFilterCity(e.target.value);
                      setListPage(1);
                    }}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      padding: "7px 12px",
                      fontSize: 13,
                      color: "#374151",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">All Cities</option>
                    {filterCityOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    style={{
                      background: tealLight,
                      color: teal,
                      border: `1px solid ${teal}`,
                      borderRadius: 8,
                      padding: "7px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Reset filter
                  </button>
                </div>
              </div>

              {/* Content */}
              {listLoading ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div className="spinner-border" style={{ color: teal }} />
                  <div
                    style={{ marginTop: 10, fontSize: 13, color: "#6b7280" }}
                  >
                    Loading...
                  </div>
                </div>
              ) : listData.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "48px 0",
                    color: "#9ca3af",
                  }}
                >
                  <i
                    className="bi bi-link-45deg"
                    style={{ fontSize: 40, display: "block", marginBottom: 12 }}
                  />
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    No landing pages saved yet
                  </div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    Fill the form and save your first landing page
                  </div>
                </div>
              ) : (
                <>
                  {/* Cards grid */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {listData.map((row, idx) => {
                      const rowKey = row.id ?? idx;
                      const date = row.created_at ?? row.createdAt;
                      const url =
                        row.url ?? row.generated_url ?? row.generatedUrl;
                      const clinicDisplay = getClinicDisplay(row);
                      const sourceLabel =
                        AD_SOURCES.find((s) => s.value === row.source)?.label ??
                        row.source ??
                        "—";
                      const copyableUrl = buildCopyableLandingUrl(
                        row,
                        domainOptions
                      );

                      return (
                        <div
                          key={rowKey}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: "16px 18px",
                            background: "#fafafa",
                            transition: "box-shadow 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            (
                              e.currentTarget as HTMLDivElement
                            ).style.boxShadow = "0 2px 10px rgba(0,0,0,0.07)";
                          }}
                          onMouseLeave={(e) => {
                            (
                              e.currentTarget as HTMLDivElement
                            ).style.boxShadow = "none";
                          }}
                        >
                          {/* Card top row: source badge + date + edit */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: 10,
                            }}
                          >
                            <span
                              style={{
                                background: "#e0f2fe",
                                color: "#0369a1",
                                borderRadius: 6,
                                padding: "3px 10px",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {sourceLabel}
                            </span>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              {date && (
                                <span
                                  style={{ fontSize: 12, color: "#9ca3af" }}
                                >
                                  <i
                                    className="bi bi-calendar3"
                                    style={{ marginRight: 4 }}
                                  />
                                  {new Date(date).toLocaleDateString("en-GB")}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  handleCopyLandingUrl(row, rowKey)
                                }
                                disabled={!copyableUrl}
                                title={
                                  copyableUrl ||
                                  "Need city and domain to build URL"
                                }
                                style={{
                                  background: copiedRowKey === rowKey
                                    ? "#dcfce7"
                                    : "#fff",
                                  color: copiedRowKey === rowKey
                                    ? "#15803d"
                                    : "#374151",
                                  border: `1px solid ${
                                    copiedRowKey === rowKey ? "#86efac" : "#d1d5db"
                                  }`,
                                  borderRadius: 6,
                                  padding: "3px 10px",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: copyableUrl ? "pointer" : "not-allowed",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  opacity: copyableUrl ? 1 : 0.5,
                                }}
                              >
                                <i
                                  className={
                                    copiedRowKey === rowKey
                                      ? "bi bi-check2"
                                      : "bi bi-clipboard"
                                  }
                                  style={{ fontSize: 11 }}
                                />
                                {copiedRowKey === rowKey ? "Copied" : "Copy URL"}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEdit(row)}
                                title="Edit"
                                style={{
                                  background: tealLight,
                                  color: teal,
                                  border: `1px solid ${teal}`,
                                  borderRadius: 6,
                                  padding: "3px 10px",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <i
                                  className="bi bi-pencil"
                                  style={{ fontSize: 11 }}
                                />
                                Edit
                              </button>
                            </div>
                          </div>

                          {/* City · Domain · Zone pills */}
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 6,
                              marginBottom: 10,
                            }}
                          >
                            {row.city && (
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  background: tealLight,
                                  color: teal,
                                  borderRadius: 20,
                                  padding: "3px 10px",
                                  fontSize: 12,
                                  fontWeight: 500,
                                }}
                              >
                                <i
                                  className="bi bi-geo-alt-fill"
                                  style={{ fontSize: 11 }}
                                />
                                {row.city}
                              </span>
                            )}
                            {row.domain && (
                              <span
                                style={{
                                  background: "#f3e8ff",
                                  color: "#7c3aed",
                                  borderRadius: 20,
                                  padding: "3px 10px",
                                  fontSize: 12,
                                  fontWeight: 500,
                                }}
                              >
                                {resolveDomainTitle(row, domainOptions)}
                              </span>
                            )}
                            {row.zone && (
                              <span
                                style={{
                                  background: "#fef3c7",
                                  color: "#92400e",
                                  borderRadius: 20,
                                  padding: "3px 10px",
                                  fontSize: 12,
                                  fontWeight: 500,
                                }}
                              >
                                Zone {row.zone}
                              </span>
                            )}
                          </div>

                          {/* Clinics */}
                          <div style={{ marginBottom: url ? 10 : 0 }}>
                            <div
                              style={{
                                fontSize: 11,
                                color: "#9ca3af",
                                fontWeight: 500,
                                marginBottom: 3,
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              Clinics
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: "#374151",
                                lineHeight: 1.5,
                              }}
                            >
                              {clinicDisplay}
                            </div>
                          </div>

                          {/* URL */}
                          {url && (
                            <div
                              style={{
                                background: "#f1f5f9",
                                borderRadius: 6,
                                padding: "8px 12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  color: "#475569",
                                  wordBreak: "break-all",
                                  flex: 1,
                                }}
                              >
                                {url}
                              </span>
                              <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: teal, flexShrink: 0 }}
                                title="Open URL"
                              >
                                <i
                                  className="bi bi-box-arrow-up-right"
                                  style={{ fontSize: 14 }}
                                />
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: 20,
                    }}
                  >
                    <span style={{ fontSize: 13, color: "#6b7280" }}>
                      Showing {(listPage - 1) * RECORD_PER_PAGE + 1}–
                      {Math.min(listPage * RECORD_PER_PAGE, listTotal)} of{" "}
                      {listTotal}
                    </span>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <button
                        onClick={() => setListPage((p) => Math.max(1, p - 1))}
                        disabled={listPage === 1}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          padding: "5px 12px",
                          background: "#fff",
                          cursor: listPage === 1 ? "not-allowed" : "pointer",
                          color: listPage === 1 ? "#d1d5db" : "#374151",
                          fontSize: 13,
                        }}
                      >
                        <i className="bi bi-chevron-left" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(
                          (p) =>
                            p === 1 ||
                            p === totalPages ||
                            Math.abs(p - listPage) <= 1
                        )
                        .reduce<(number | "…")[]>((acc, p, i, arr) => {
                          if (i > 0 && (arr[i - 1] as number) < p - 1)
                            acc.push("…");
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, i) =>
                          p === "…" ? (
                            <span
                              key={`ellipsis-${i}`}
                              style={{
                                fontSize: 13,
                                color: "#9ca3af",
                                padding: "0 4px",
                              }}
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => setListPage(p as number)}
                              style={{
                                border: `1px solid ${
                                  p === listPage ? teal : "#d1d5db"
                                }`,
                                borderRadius: 6,
                                padding: "5px 10px",
                                fontSize: 13,
                                background: p === listPage ? teal : "#fff",
                                color: p === listPage ? "#fff" : "#374151",
                                cursor: "pointer",
                                fontWeight: p === listPage ? 600 : 400,
                                minWidth: 32,
                              }}
                            >
                              {p}
                            </button>
                          )
                        )}
                      <button
                        onClick={() =>
                          setListPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={listPage === totalPages}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          padding: "5px 12px",
                          background: "#fff",
                          cursor:
                            listPage === totalPages ? "not-allowed" : "pointer",
                          color:
                            listPage === totalPages ? "#d1d5db" : "#374151",
                          fontSize: 13,
                        }}
                      >
                        <i className="bi bi-chevron-right" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
